import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

import { API_BASE as API } from '../utils/api';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/api/alerts/subscribe-email`, { email: email.trim() });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  const columns = [
    {
      title: 'Product',
      links: [
        { label: 'Map Search', to: '/map' },
        { label: 'List Search', to: '/search' },
        { label: 'Pricing', to: '/pricing' },
        { label: 'Letter Templates', to: '/letters' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Council Directory', to: '/planning-applications' },
        { label: 'Planning Guides', to: '/planning-permission' },
        { label: 'AI Intelligence', to: '/intelligence' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About', to: '/about' },
        { label: 'Contact', to: '/contact' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms & Conditions', to: '/terms' },
        { label: 'Privacy Policy', to: '/privacy' },
        { label: 'Disclaimer', to: '/disclaimer' },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer">
      {/* Email Capture CTA */}
      <div className="bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <div className="max-w-xl mx-auto text-center md:text-left md:max-w-none md:flex md:items-center md:justify-between md:gap-8">
            <div className="mb-6 md:mb-0">
              <h3 className="font-['Manrope'] text-base sm:text-lg md:text-xl font-bold text-white mb-1.5">
                Never miss a planning application
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
                Get free weekly alerts for new projects in your area. Straight to your inbox, no spam.
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:min-w-[380px]" data-testid="footer-email-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.uk"
                className="flex-1 min-w-0 px-4 py-2.5 bg-white/10 border border-white/15 text-white placeholder:text-zinc-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-md"
                data-testid="footer-email-input"
                required
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg text-sm transition-all disabled:opacity-50 inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                data-testid="footer-subscribe-btn"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>Subscribe <ArrowRight className="w-3.5 h-3.5" /></>
                )}
              </button>
            </form>
          </div>
          {status === 'success' && (
            <p className="text-emerald-400 text-sm mt-3 text-center md:text-right" data-testid="footer-subscribe-success">
              You're subscribed. We'll send your first alert soon.
            </p>
          )}
          {status === 'error' && (
            <p className="text-red-400 text-sm mt-3 text-center md:text-right" data-testid="footer-subscribe-error">
              Something went wrong. Please try again.
            </p>
          )}
        </div>
      </div>

      {/* Links Grid */}
      <div className="bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-1 mb-4 md:mb-0">
              <Link to="/" className="flex items-center gap-2 mb-3" data-testid="footer-logo">
                <div className="w-6 h-6 bg-emerald-700 rounded-md flex items-center justify-center">
                  <Search className="w-3 h-3 text-white" />
                </div>
                <span className="font-['Manrope'] font-extrabold text-zinc-800 text-sm">PlanningSearch</span>
              </Link>
              <p className="text-xs text-zinc-500 leading-relaxed max-w-[200px]">
                UK planning application intelligence for builders, architects & tradespeople.
              </p>
            </div>

            {/* Nav columns */}
            {columns.map((col) => (
              <div key={col.title}>
                <h4 className="font-['Manrope'] text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-3">
                  {col.title}
                </h4>
                <ul className="space-y-2">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="text-sm text-zinc-600 hover:text-emerald-700 transition-colors"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-400">
            &copy; {new Date().getFullYear()} PlanningSearch Ltd. All rights reserved.
          </p>
          <p className="text-[11px] text-zinc-400 text-center md:text-right max-w-lg leading-relaxed">
            Planning data sourced from public council records. PlanningSearch does not guarantee completeness or real-time accuracy.{' '}
            <Link to="/disclaimer" className="underline hover:text-emerald-600">Read our disclaimer</Link>.
          </p>
        </div>
      </div>
    </footer>
  );
}
